<template>
  <div v-on-clickaway="hideDropdown" class="personal-counter">
    <EPill
      ref="toggle"
      v-focus="open"
      class="personal-counter__pill"
      data-track-id="personal-counter"
      data-test-id="personal-counter-toggle"
      @click="toggleDropdown"
    >
      <TreeSyncedIcon v-if="loggedIn" id="tree-synced-icon" />
      <TreeIcon v-else />
      <span class="sr-only">Trees you planted: </span>
      <span
        class="personal-counter__text notranslate"
        data-test-id="personal-counter-text"
      >{{ count }}</span>
    </EPill>
    <EDropdown
      :visible="open"
      class="personal-counter__dropdown"
      data-test-id="personal-counter-dropdown"
      side="right"
    >
      <EPersonalCounterContent />
    </EDropdown>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import { mixin as clickaway } from 'vue-clickaway';
import { mixin as focus } from 'vue-focus';

import { sendEvent } from '~~/common/js/client/analytics';
import EDropdown from '~~/common/vue/components/dropdown';
import EPill from '~~/common/vue/components/pill';
import TreeIcon from '~~/common/vue/components/icons/m/tree-color.svg';
import TreeSyncedIcon from '~~/common/vue/components/icons/m/tree-color-synced.svg';
import EPersonalCounterContent from './content';

// TODO: We need to re-think how we want to implement component specific tracking
// adding TRACK_IDs to the component directly makes it difficult to re-use the components.
// It also should not be the concern of the component whats beeing tracked
export const TRACK_IDS = {
  beta: {
    loggedIn: {
      display: 'personal-counter-content--loggedin',
    },
    loggedOut: {
      display: 'personal-counter-content--loggedout',
    },
  },
  default: {
    display: 'personal-counter-content',
  },
};

export default {
  name: 'EPersonalCounter',
  components: {
    EDropdown,
    EPersonalCounterContent,
    EPill,
    TreeIcon,
    TreeSyncedIcon,
  },
  mixins: [clickaway, focus],
  props: {
    active: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    open: false,
  }),
  computed: {
    ...mapState('global', [
      'locale',
    ]),
    ...mapState('user', [
      'betaUser',
      'loggedIn',
      'treeCount',
    ]),
    count() {
      return this.treeCount > 0 ? Number(this.treeCount).toLocaleString(this.locale) : 0;
    },
    trackIds() {
      if (this.betaUser) {
        return this.loggedIn ? TRACK_IDS.beta.loggedIn : TRACK_IDS.beta.loggedOut;
      }

      return TRACK_IDS.default;
    },
  },
  beforeMount() {
    this.open = this.active;
  },
  mounted() {
    this.trackDisplay();
  },
  methods: {
    toggleDropdown() {
      this.open = !this.open;
      this.$emit('dropdown-toggled', { open: this.open });
      this.trackDisplay();
    },
    hideDropdown({ target }) {
      const { $el } = this.$refs.toggle;
      if (target !== $el && !$el.contains(target)) {
        this.open = false;
        this.$emit('dropdown-toggled', { open: this.open });
      }
    },
    trackDisplay() {
      if (this.open) {
        sendEvent(this.trackIds.display);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.personal-counter {
  @include desktop {
    position: relative;
  }
}

.personal-counter > .personal-counter__dropdown {
  top: $space-4l + $space-s;
  right: $space-s;
  width: 300px;

  @include tablet {
    @include elevation(2);

    width: 300px;
    border-radius: $border-radius-s;
    background-color: $white;
  }

  @include desktop {
    border: solid $border-width $color-border;
    margin-top: $space-s;
    top: 100%;
    right: -1 * $space-s;
  }
}

.personal-counter__pill {
  align-items: center;
  border-color: $transparent;
  display: flex;
  flex-direction: column;
  height: auto;

  @include desktop {
    flex-direction: row;
    // TODO: this isn't pretty. Check if the Pill
    // component always needs to be borderless on mobile
    // and if so, move this styling there.
    border-color: $color-border;
    height: $space-3l;
  }
}

.personal-counter__text {
  color: $color-text-primary;
  font-size: $font-1s;
  // can't use a standard spacing value here
  // to achieve the correct spacing between the icon and number
  margin: -3px 0 0 0;
  vertical-align: middle;

  @include desktop {
    margin: 0 $space-1s 0 $space-1s;
  }
}
</style>

<docs>
```js
// hacky function for demo purposes
const onDropdownToggled = ({ open }) => {
  document.getElementById('test-message').innerText =
    `Dropdown is ${open ? 'open' : 'closed'}`;
};

<EPersonalCounter
  :tree-count="345"
  @dropdown-toggled="onDropdownToggled"
/>

<p id="test-message">Dropdown is closed</p>
```
</docs>
